<template>
  <DialogForm
    :model-value="modelValue"
    title="Enregistrer le motif de la perte"
    size="medium"
    :loading="saving"
    save-label="Enregistrer et terminer l'appel"
    saving-label="Enregistrement…"
    save-variant="danger"
    save-icon="fas fa-ban"
    :save-disabled="!selectedId || saving"
    :dirty="dirty"
    data-testid="not-interested-modal"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('update:modelValue', false)"
    @save="handleConfirm"
  >
    <p class="intro">Sélectionnez le motif pour lequel le client n'est pas intéressé.</p>

    <LoadingSpinner v-if="loading" />

    <EmptyState
      v-else-if="!categories.length"
      compact
      icon="fas fa-triangle-exclamation"
      message="Aucun motif de perte n'est configuré. Rechargez la page ou contactez un administrateur."
    />

    <template v-else>
      <DialogSection v-for="group in categories" :key="group.category" :title="group.label">
        <!-- Radio-cards générées dynamiquement à partir des motifs : pas des
             champs statiques, donc pas de DialogRow — un motif par ligne. -->
        <label
          v-for="reason in group.reasons"
          :key="reason.id"
          class="reason-option"
          :class="{ selected: selectedId === reason.id }"
        >
          <input
            type="radio"
            name="loss-reason"
            :value="reason.id"
            v-model="selectedId"
            class="reason-radio"
          />
          <span>{{ reason.label }}</span>
        </label>
      </DialogSection>
    </template>

    <Textarea
      v-model="comment"
      label="Commentaire (optionnel)"
      :rows="3"
      placeholder="Précisions sur le motif de la perte…"
    />

    <Checkbox v-model="doNotContact" variant="card">
      <strong>Ne pas relancer</strong> — bloque les futurs emails à ce client
      (obligation légale si le client en fait la demande).
    </Checkbox>
  </DialogForm>
</template>

<script setup>
/**
 * Motif de perte — signalé « logique métier à sortir » : le chargement des
 * motifs (appel réseau + toast d'erreur) reste ici volontairement, hors
 * périmètre de cette migration (cf. docs/MODALES-V2.md, M10).
 */
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import { DialogForm, DialogSection, LoadingSpinner, EmptyState, Textarea, Checkbox } from '../ui'
import { useDialogGuardOn } from '../../composables/ui/useDialogGuard.js'
import { lossReasonsService } from '../../services/lossReasonsService.js'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  saving: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const categories = ref([])
const loading = ref(false)
const selectedId = ref(null)
const comment = ref('')
const doNotContact = ref(false)
let loaded = false

async function loadReasons() {
  if (loaded) return
  loading.value = true
  try {
    const data = await lossReasonsService.list()
    categories.value = data.categories || []
    loaded = true
  } catch (e) {
    toast.error('Impossible de charger les motifs de perte')
    console.error(e)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      selectedId.value = null
      comment.value = ''
      doNotContact.value = false
      loadReasons()
    }
  }
)

// Pris après la réinitialisation ci-dessus : une fenêtre rouverte ne part pas
// déjà « modifiée ».
const formSnapshot = computed(() => ({
  selectedId: selectedId.value,
  comment: comment.value,
  doNotContact: doNotContact.value
}))
const { dirty } = useDialogGuardOn(
  computed(() => props.modelValue),
  formSnapshot
)

const handleConfirm = () => {
  if (!selectedId.value) return
  emit('confirm', {
    lossReasonId: selectedId.value,
    comment: comment.value.trim() || null,
    doNotContact: doNotContact.value
  })
}
</script>

<style scoped>
.intro {
  color: var(--color-text-primary);
  line-height: 1.5;
  margin: 0;
}

/* Radio-card propre à cette fenêtre : ni DialogRow (ce n'est pas une grille de
   champs) ni un composant du kit (les motifs sont générés dynamiquement). */
.reason-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-secondary);
  transition: all var(--transition-base);
}

.reason-option:hover {
  border-color: var(--color-primary);
}

.reason-option.selected {
  border-color: var(--color-danger);
  background: var(--color-danger-lighter, rgba(214, 69, 69, 0.08));
}

.reason-radio {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
}

.reason-option span {
  color: var(--color-text-primary);
  line-height: 1.4;
}
</style>
